import { Trans, useLingui } from "@lingui/react/macro";
import { useDebouncedValue } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { api } from "@/lib/api-client";
import usePreferredLanguageStore from "@/stores/usePreferredLanguageStore";
import { useForm } from "@mantine/form";
import { AuthFormWrapper } from "../AuthFormWrapper";
import { addToast, Button } from "@heroui/react";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { TurnstileCompat } from "@/Components/TurnstileCompat";
import { cn } from "@/utils";
import { markSignupCompleted } from "@/utils/signupTracking";
import { parseAsString, parseAsStringLiteral, useQueryState } from "nuqs";
import { rememberAppliedPromoCode } from "@/features/self-onboarding/appliedPromoCode";
import { OnboardingPromoCode } from "@/features/self-onboarding/OnboardingPromoCode";
import {
  ReferralCoupon,
  ReferralSignupHeading,
  ReferralSignupReassurance,
} from "@/features/self-onboarding/ReferralSignupIntro";
import { CustomerAppDownload } from "./CustomerAppDownload";
import { InAppBrowserNotice } from "./InAppBrowserNotice";
import { RegisterRoleChoice } from "./RegisterRoleChoice";
import {
  detectDevicePlatform,
  getDirectStoreUrl,
  pickLinkCode,
  REGISTER_ROLES,
  type RegisterRole,
  resolveRegisterRole,
} from "./registerRole";

// The check is rate limited per address, so it waits for a pause in typing and
// for enough characters to be a plausible code.
const PROMO_CODE_DEBOUNCE_MS = 700;
const MIN_PROMO_CODE_LENGTH = 4;

export const Register = () => {
  const { t } = useLingui();
  const {
    mutateAsync: register,
    isPending,
    processedErrorMessage,
  } = api.auth.useRegister();
  const { preferredLanguage } = usePreferredLanguageStore((state) => state);
  const [roleFromLink, setRole] = useQueryState(
    "role",
    parseAsStringLiteral(REGISTER_ROLES).withOptions({ history: "push" }),
  );

  // A referral link carries the code: /register?referralCode=ANA50
  const [referralCodeFromLink] = useQueryState("referralCode", parseAsString);
  const [legacyCodeFromLink] = useQueryState("code", parseAsString);
  const codeFromLink = pickLinkCode(referralCodeFromLink, legacyCodeFromLink);
  const role = resolveRegisterRole(roleFromLink, codeFromLink);

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [showTurnstile, setShowTurnstile] = useState(false);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const isTurnstileDisabled = import.meta.env.VITE_DISABLE_TURNSTILE === "true";

  const form = useForm({
    initialValues: {
      email: "",
      language: "sl",
      promoCode: codeFromLink ?? "",
    },
    validate: {
      email: (value) =>
        /^\S+@\S+$/.test(value.trim()) ? null : t`Neveljaven e-poštni naslov`,
    },
  });

  const {
    mutateAsync: checkPromoCode,
    isPending: isCheckingPromoCode,
    isError: isPromoCodeCheckError,
    data: promoCodeCheck,
    reset: resetPromoCodeCheck,
  } = api.promoCodes.useCheckPromoCode();

  const [debouncedPromoCode] = useDebouncedValue(
    form.values.promoCode.trim(),
    PROMO_CODE_DEBOUNCE_MS,
  );

  useEffect(() => {
    if (debouncedPromoCode.length < MIN_PROMO_CODE_LENGTH) {
      resetPromoCodeCheck();
      return;
    }

    // A failed check is not fatal: sign-up validates the code again.
    checkPromoCode({ code: debouncedPromoCode }).catch(() => undefined);
  }, [debouncedPromoCode, checkPromoCode, resetPromoCodeCheck]);

  const onSubmitForm = async (values: typeof form.values) => {
    if (!turnstileToken && !isTurnstileDisabled) {
      // The referral page hides the widget until it needs a click; reveal it so the user sees why.
      setShowTurnstile(true);
      addToast({
        title: t`Napaka`,
        description: t`Verifikacija z Captcha ni uspela. Osvežite stran in poskusite znova.`,
        color: "warning",
      });
      setTurnstileToken(null);
      setTurnstileKey((prev) => prev + 1);
      return;
    }

    const registerData = {
      email: values.email.trim(),
      language: preferredLanguage.userPreferredLanguage,
      turnstileToken: turnstileToken || "",
      promoCode: values.promoCode.trim() || undefined,
    };

    try {
      const response = await register(registerData);

      markSignupCompleted(preferredLanguage.userPreferredLanguage);

      rememberAppliedPromoCode(response.promoCode);

      localStorage.setItem("usr_token", response.token);
      // navigate(0);
      window.location.reload();
    } catch (error) {
      addToast({
        title: t`Napaka`,
        color: "danger",
      });
    }

    if (!isTurnstileDisabled) {
      setTurnstileToken(null);
      setTurnstileKey((prev) => prev + 1);
    }
  };

  const pageTitle = t`Registracija`;
  const hasLinkCode =
    !!codeFromLink && codeFromLink.length >= MIN_PROMO_CODE_LENGTH;
  // Once the user edits the code in the fallback field it is no longer the link
  // code, so the field stays put instead of flipping back to the referral layout.
  const showReferralIntro =
    hasLinkCode &&
    form.values.promoCode.trim() === codeFromLink &&
    !isPromoCodeCheckError &&
    promoCodeCheck?.valid !== false;

  function handleRoleSelect(selected: RegisterRole) {
    void setRole(selected);

    if (selected !== "customer") return;

    const storeUrl = getDirectStoreUrl(detectDevicePlatform());

    if (storeUrl) window.location.assign(storeUrl);
  }

  // Skipped by `/register?role=business` or by a link that carries a code.
  if (role !== "business") {
    return (
      <>
        <title>{pageTitle}</title>
        <AuthFormWrapper isLogin={false}>
          {role === "customer" ? (
            <CustomerAppDownload onBack={() => void setRole(null)} />
          ) : (
            <RegisterRoleChoice onSelect={handleRoleSelect} />
          )}
        </AuthFormWrapper>
      </>
    );
  }

  const formFields = (
    <>
      <LimeInput
        label={t`E-poštni naslov`}
        data-identifier={"register-email-input"}
        size="sm"
        className="w-full"
        {...form.getInputProps("email")}
        autoComplete="email"
        name="email"
        error={form.errors.email}
      />

      {!showReferralIntro && (
        <OnboardingPromoCode
          inputProps={form.getInputProps("promoCode")}
          isChecking={isCheckingPromoCode}
          result={promoCodeCheck}
        />
      )}

      {!isTurnstileDisabled && (
        <TurnstileCompat
          key={turnstileKey}
          siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
          // Still verifies everyone; the widget only shows when Cloudflare needs a click.
          options={
            hasLinkCode && !showTurnstile
              ? { appearance: "interaction-only" }
              : undefined
          }
          className={hasLinkCode ? "self-center" : undefined}
          onSuccess={(token) => setTurnstileToken(token)}
          onExpire={() => setTurnstileToken(null)}
          onError={(error) => {
            console.error(error);
            setTurnstileToken(null);
            setShowTurnstile(true);
          }}
        />
      )}

      <LimeAlert message={processedErrorMessage} />

      <Button
        className={cn("w-full", !showReferralIntro && "mt-2")}
        type="submit"
        isLoading={isPending}
        data-identifier="continue-button"
        color="primary"
      >
        {showReferralIntro && promoCodeCheck?.valid ? (
          <Trans>Nadaljuj s popustom</Trans>
        ) : (
          <Trans>Nadaljuj</Trans>
        )}
      </Button>
    </>
  );

  return (
    <>
      <title>{pageTitle}</title>
      <AuthFormWrapper
        isLogin={false}
        legalAtBottom={showReferralIntro}
        heading={showReferralIntro ? <ReferralSignupHeading /> : undefined}
      >
        <form
          style={showReferralIntro ? undefined : { display: "contents" }}
          className={
            showReferralIntro
              ? "mx-auto flex w-full max-w-sm flex-col gap-3"
              : undefined
          }
          onSubmit={form.onSubmit(onSubmitForm)}
          noValidate
        >
          <InAppBrowserNotice />

          <ReferralCoupon
            active={showReferralIntro}
            promoCode={
              promoCodeCheck?.valid ? promoCodeCheck.promoCode : undefined
            }
          >
            {formFields}
          </ReferralCoupon>

          {showReferralIntro && <ReferralSignupReassurance />}
        </form>
      </AuthFormWrapper>
    </>
  );
};
